import React, { useMemo, useState } from "react";
import PageShell, { PageHeader } from "@/components/PageShell";
import ReviewsSlider from "@/components/ReviewsSlider";
import { PROJECTS, SERVICES } from "@/data/site";

const PROJECT_SERVICE = ["lemezfedes", "cserepteto", "lemezfedes", "tetofelujitas", "lemezfedes", "tetoszerkezet", "cserepteto", "tetoszerkezet"];
const FILTERS = [{ id: "all", label: "Mind" }, ...SERVICES.map((s) => ({ id: s.slug, label: s.shortTitle }))];

const CASE_BEFORE = [
  "Felállított tetőszerkezet, állványzaton",
  "Szarufák és taréjszelemen a helyén",
  "Fedés és alátétfólia nélkül",
  "Bádogos szegélyek, ereszek hiányoznak",
  "Kéménykáva még szabadon",
];
const CASE_AFTER = [
  "Teljes cseréptetős fedés",
  "Bádogos ereszcsatorna és szegélyek",
  "Szakszerű kéménybekötés",
  "Szellőzött, párafékes rétegrend",
  "Hófogó és vápalemez beépítve",
];

export default function ReferenciakPage() {
  const [filter, setFilter] = useState("all");
  const filtered = useMemo(() => {
    return PROJECTS.map((p, i) => ({ ...p, srv: PROJECT_SERVICE[i] }))
      .filter((p) => filter === "all" || p.srv === filter);
  }, [filter]);

  return (
    <PageShell>
      <PageHeader
        number="03"
        eyebrow="Referenciák"
        title="Néhány munka"
        titleAccent="a portfólióból."
        lead="Bármelyik ingatlanra szívesen elvisszük személyesen — a tulajdonosok szívesen beszélnek a tapasztalatukról."
      />

      {/* Filter + grid */}
      <section className="section" style={{ background: "var(--ink)" }}>
        <div className="site-container">
          <div style={{ display: "flex", gap: 8, marginBottom: 32, flexWrap: "wrap" }} data-testid="ref-filters">
            {FILTERS.map((f) => (
              <button
                key={f.id} onClick={() => setFilter(f.id)}
                className={`chip ${filter === f.id ? "active" : ""}`}
                data-testid={`ref-filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 16, minHeight: 240,
          }} data-testid="ref-grid">
            {filtered.map((p, idx) => (
              <article key={p.title} data-testid={`project-card-${idx}`} className="tile" style={{ aspectRatio: "4 / 3", minHeight: 240 }}>
                <div
                  className="tile-img"
                  style={{
                    position: "absolute", inset: 0,
                    backgroundImage: `url("${p.photo}")`,
                    backgroundSize: "cover", backgroundPosition: "center",
                  }}
                />
                <span
                  className="eyebrow"
                  style={{
                    position: "absolute", top: 16, left: 16, zIndex: 2,
                    background: "var(--ink)", padding: "5px 10px",
                    border: "1px solid var(--gold-deep)",
                    fontSize: 10,
                  }}
                >
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <div style={{
                  position: "absolute", left: 0, right: 0, bottom: 0, zIndex: 2,
                  padding: "32px 16px 16px",
                  background: "linear-gradient(to top, rgba(11,10,9,0.88) 0%, rgba(11,10,9,0) 100%)",
                }}>
                  <span style={{ fontFamily: "Manrope, sans-serif", fontWeight: 600, fontSize: 13.5, color: "var(--text)", letterSpacing: "-0.01em" }}>
                    {p.title}
                  </span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Case study deep-dive — Before / After */}
      <section className="section bg-grid" style={{ background: "var(--ink-2)", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)", position: "relative" }}>
        <div className="site-container" style={{ position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <span style={{ height: 1, background: "var(--gold)", width: 32 }} />
            <span className="eyebrow">Esettanulmány — Rönkház</span>
          </div>
          <h2 className="h2" style={{ margin: 0, marginBottom: 12, maxWidth: 720 }}>
            Rönkház, <span style={{ color: "var(--gold)" }}>a szerkezettől a kész tetőig.</span>
          </h2>
          <p className="lead" style={{ marginTop: 0, marginBottom: 48, maxWidth: 600 }}>
            Egy rönkház teljes tetőmunkája — az ácsszerkezettől a kész fedésig és bádogozásig.
          </p>

          <div className="case-grid" style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16 }}>
            <style>{`@media (min-width: 900px) { .case-grid { grid-template-columns: 1fr 1fr !important; gap: 24px !important; } }`}</style>

            {[{ label: "Előtte", img: "/projects/proj-6.jpg", items: CASE_BEFORE, accent: "var(--text-dim)" },
              { label: "Utána",  img: "/projects/proj-7.jpg", items: CASE_AFTER,  accent: "var(--gold)" }].map((col, i) => (
              <div key={i} data-testid={`case-${i === 0 ? "before" : "after"}`} className="dark-card" style={{ padding: 0, overflow: "hidden" }}>
                <div style={{
                  aspectRatio: "16 / 9", position: "relative",
                  backgroundImage: `url("${col.img}")`,
                  backgroundSize: "cover", backgroundPosition: "center",
                }}>
                  <div style={{
                    position: "absolute", inset: 0,
                    background: "linear-gradient(to top, rgba(11,10,9,0.55) 0%, rgba(11,10,9,0) 55%)",
                  }} />
                  <span className="eyebrow-muted" style={{
                    position: "absolute", top: 14, left: 14, zIndex: 2, color: col.accent,
                    background: "var(--ink)", padding: "5px 10px", border: `1px solid ${col.accent}`,
                  }}>
                    {col.label}
                  </span>
                </div>
                <div style={{ padding: 28, borderTop: "1px solid var(--line)" }}>
                  <span className="eyebrow-muted" style={{ color: col.accent, display: "block", marginBottom: 16 }}>
                    {i === 0 ? "Mit találtunk" : "Mit építettünk"}
                  </span>
                  <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                    {col.items.map((item, idx) => (
                      <li key={idx} style={{ display: "flex", alignItems: "flex-start", gap: 12, color: "var(--text-mid)", fontSize: 14, lineHeight: 1.55 }}>
                        <span aria-hidden style={{ width: 5, height: 5, background: col.accent, marginTop: 8, flexShrink: 0 }} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews slider */}
      <section className="section" style={{ background: "var(--ink)" }}>
        <div className="site-container">
          <ReviewsSlider />
        </div>
      </section>
    </PageShell>
  );
}
